import { interpolate, useCurrentFrame, useVideoConfig } from 'remotion';

// Fonts
import { interFontFamily, michromaFontFamily } from '@/styles/fonts';

// Types
import type { StatBoxProps } from '../../types';

// Constants
import {
  ANIMATION_DURATIONS,
  ENTRANCE_ANIMATION,
  ROTATION_ANGLE,
} from '../../constants';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { combineClasses } from '@/utils';
import {
  getEntranceAndFloatAnimation,
  calculateSheenPosition,
} from '../../utils';

export const StatBox = ({
  className,
  count,
  label,
  style,
  animationDelay = 0,
  floatDuration = 4,
  floatDirection = 'up',
  entranceDirection = 'fromTop',
  exitProgress = 0,
}: StatBoxProps) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  let { translateY, rotateX, opacity } = getEntranceAndFloatAnimation(
    frame,
    fps,
    animationDelay,
    floatDuration,
    floatDirection,
    entranceDirection,
  );

  // Reverse entrance animation when exiting
  if (exitProgress > 0) {
    const targetY = ENTRANCE_ANIMATION.translateY[
      entranceDirection
    ][0] as number;
    const targetRotateX = ENTRANCE_ANIMATION.rotateX[
      entranceDirection
    ][0] as number;

    translateY = interpolate(exitProgress, [0, 1], [translateY, targetY]);
    rotateX = interpolate(exitProgress, [0, 1], [rotateX, targetRotateX]);
    opacity = interpolate(exitProgress, [0, 1], [opacity, 0]);
  }

  const sheenPosition = calculateSheenPosition(
    frame,
    fps,
    animationDelay,
    ANIMATION_DURATIONS.entrance,
  );

  return (
    <div
      className={combineClasses(
        'flex-center absolute flex-col overflow-hidden bg-secondary-250 py-10 text-white',
        className,
      )}
      style={{
        ...style,
        transform: `rotate(${ROTATION_ANGLE}) translateY(${translateY}px) rotateX(${rotateX}deg)`,
        transformStyle: 'preserve-3d',
        opacity,
      }}
    >
      {/* Sheen effect overlay */}
      <div
        className="pointer-events-none absolute inset-0 size-full"
        style={{
          background: employeeYearInReviewTheme.rawColors.sheenGradient,
          transform: `translateX(${sheenPosition}%)`,
        }}
      />

      <div
        className="font-black"
        style={{
          fontSize: style?.fontSize,
          fontFamily: interFontFamily,
          lineHeight: style?.lineHeight ?? 1,
        }}
      >
        {count}
      </div>
      <div
        className="whitespace-nowrap text-center text-5xl tracking-normal"
        style={{
          fontFamily: michromaFontFamily,
        }}
      >
        {label}
      </div>
    </div>
  );
};
